import { FC, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ClientColumn } from "./ClientColumn";

import { PopoverContent } from "@/components/ui/v2/popover";
import { ROUTES } from "@/constants/routes";
import { getOverviewPageForPath } from "@/lib/utils/clientRouteUtils";

type ClientSelectorProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const ClientSelector: FC<ClientSelectorProps> = ({
  open,
  onOpenChange,
}) => {
  const [clientSearch, setClientSearch] = useState("");
  const clientSearchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId } = useParams();

  // Reset search when popover closes so the list is clean on reopen.
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (!open) {
      setClientSearch("");
    }
  }

  const handleClientSelect = (clientId2: string, clientName: string) => {
    const pathParts = location.pathname.split("/");
    const currentClientIndex = pathParts.indexOf(clientId ?? "");
    const hasClientInPath = clientId && currentClientIndex !== -1;

    if (hasClientInPath) {
      const overviewPage = getOverviewPageForPath(location.pathname, clientId);
      navigate(`/${ROUTES.clients}/${clientId2}/${overviewPage}`, {
        state: { clientName },
      });
    } else {
      navigate(`/${ROUTES.clients}/${clientId2}/${ROUTES.campaigns}`, {
        state: { clientName },
      });
    }
    onOpenChange(false);
    setClientSearch("");
  };

  const handleCreateClientClick = () => {
    onOpenChange(false);
    navigate(`/${ROUTES.clients}/${ROUTES.new}`);
  };

  return (
    <PopoverContent
      align="start"
      className="w-[300px] p-0"
      sideOffset={8}
      onOpenAutoFocus={(e) => {
        e.preventDefault();
        clientSearchInputRef.current?.focus();
      }}
    >
      <div className="h-[300px] overflow-hidden">
        <ClientColumn
          tenantId={null}
          clientSearch={clientSearch}
          setClientSearch={setClientSearch}
          selectedClientId={clientId}
          onClientSelect={handleClientSelect}
          onCreateClient={handleCreateClientClick}
          clientSearchInputRef={clientSearchInputRef}
        />
      </div>
    </PopoverContent>
  );
};

export { ClientSelector };
